import { Viewport } from '@/types';
import type { ResponsiveBreakpoint } from '@/stores/editor/responsive-store';

// Breakpoint width thresholds (matches CSS media queries in responsive-store.ts)
const MOBILE_MAX_WIDTH = 599;
const TABLET_MAX_WIDTH = 1023;

export type BreakpointName = 'mobile' | 'tablet' | 'desktop';

/**
 * Determines the breakpoint name from either a Viewport enum, string ID,
 * or ResponsiveBreakpoint object based on width thresholds.
 *
 * This utility solves the type mismatch issue where ResponsiveEditorCanvas
 * passes ResponsiveBreakpoint objects (e.g., {id: "iphone-14-pro", width: 393})
 * but components expect Viewport enum strings ('mobile', 'tablet', 'desktop').
 *
 * Width thresholds:
 * - Mobile: width <= 599px
 * - Tablet: 600px <= width <= 1023px
 * - Desktop: width >= 1024px
 */
export function getBreakpointFromViewport(
  viewport: Viewport | ResponsiveBreakpoint | string | undefined
): BreakpointName {
  if (!viewport) return 'desktop';

  // If it's an object with width property, use width-based detection
  if (typeof viewport === 'object' && 'width' in viewport) {
    const width = viewport.width;
    if (width <= MOBILE_MAX_WIDTH) return 'mobile';
    if (width <= TABLET_MAX_WIDTH) return 'tablet';
    return 'desktop';
  }

  // If it's a string, check for known viewport IDs
  const viewportId = typeof viewport === 'string' ? viewport : String(viewport);

  if (viewportId === Viewport.MOBILE || viewportId === 'mobile') return 'mobile';
  if (viewportId === Viewport.TABLET || viewportId === 'tablet') return 'tablet';
  if (viewportId === Viewport.DESKTOP || viewportId === 'desktop') return 'desktop';

  // Unknown string ID - default to desktop (safest fallback)
  return 'desktop';
}

/**
 * Checks if viewport represents a non-desktop breakpoint (mobile or tablet).
 * Useful for determining when to apply responsive constraints in editing mode.
 */
export function isNonDesktopViewport(
  viewport: Viewport | ResponsiveBreakpoint | string | undefined
): boolean {
  const breakpoint = getBreakpointFromViewport(viewport);
  return breakpoint !== 'desktop';
}

/**
 * Gets the viewport width from either a ResponsiveBreakpoint object or string ID.
 * Returns undefined if width cannot be determined.
 */
export function getViewportWidth(
  viewport: Viewport | ResponsiveBreakpoint | string | undefined
): number | undefined {
  if (!viewport) return undefined;

  // If it's an object with width property, return the width
  if (typeof viewport === 'object' && 'width' in viewport) {
    return viewport.width;
  }

  // For string viewport IDs, return standard widths
  const viewportId = typeof viewport === 'string' ? viewport : String(viewport);

  if (viewportId === Viewport.MOBILE || viewportId === 'mobile') return 375;
  if (viewportId === Viewport.TABLET || viewportId === 'tablet') return 768;
  if (viewportId === Viewport.DESKTOP || viewportId === 'desktop') return 1920;

  return undefined;
}
